<template>
  <div class="material-calendar">
    <div class="calendar-header">
       <button class="nav-btn" @click="prev">
         <svg width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/></svg>
       </button>
       <span class="month-label clickable" @click="toggleViewMode">{{ headerLabel }}</span>
       <button class="nav-btn" @click="next">
         <svg width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/></svg>
       </button>
    </div>

    <div class="calendar-grid" v-if="viewMode === 'month'">
      <div v-for="day in weekDays" :key="day" class="weekday-label">{{ day }}</div>
      
      <div 
        v-for="(date, index) in calendarDays" 
        :key="index"
        class="day-cell"
        :class="{
          'current-month': date.currentMonth,
          'selected': isSelected(date.date),
          'has-data': hasData(date.date),
          'today': isToday(date.date)
        }"
        @click="selectDate(date.date)"
      >
        <span class="day-number">{{ date.day }}</span>
        <div class="data-dot" v-if="hasData(date.date)"></div>
      </div>
    </div>

    <!-- YEAR GRID -->
    <div class="year-grid" v-else>
      <div 
        v-for="year in yearRange" 
        :key="year" 
        class="year-cell"
        :class="{ 'current-year': year === currentYear, 'selected': year === selectedYear }"
        @click="selectYear(year)"
      >
        {{ year }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const props = defineProps<{
  modelValue?: string; // YYYY-MM-DD
  activityDates?: string[]; // Array of YYYY-MM-DD with data
}>();

const emit = defineEmits(['update:modelValue', 'close', 'month-change']);

const today = new Date();
const initialDate = props.modelValue ? new Date(props.modelValue) : today;
// Validate initialDate
const validDate = isNaN(initialDate.getTime()) ? today : initialDate;

const currentMonth = ref(validDate.getMonth());
const currentYear = ref(validDate.getFullYear());
const viewMode = ref<'month' | 'year'>('month');
const yearPage = ref(Math.floor(currentYear.value / 12) * 12); // Grid of 12 years

const selectedYear = computed(() => {
    return props.modelValue ? new Date(props.modelValue).getFullYear() : currentYear.value;
});

const weekDays = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

const currentMonthName = computed(() => {
  return new Date(currentYear.value, currentMonth.value).toLocaleString('it-IT', { month: 'long' });
});

const headerLabel = computed(() => {
    if (viewMode.value === 'month') {
        return `${currentMonthName.value} ${currentYear.value}`;
    } else {
        return `${yearPage.value} - ${yearPage.value + 11}`;
    }
});

const yearRange = computed(() => {
    const years = [];
    for (let i = 0; i < 12; i++) {
        years.push(yearPage.value + i);
    }
    return years;
});

interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
}

const calendarDays = computed<CalendarDay[]>(() => {
  const days: CalendarDay[] = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
  
  // Adjust logic for Monday start (0=Sun -> 6, 1=Mon -> 0)
  let startDayOfWeek = firstDay.getDay() - 1; 
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  // Previous month padding
  const prevMonthLastDay = new Date(currentYear.value, currentMonth.value, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value - 1, prevMonthLastDay - i),
       day: prevMonthLastDay - i,
       currentMonth: false
     });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      date: new Date(currentYear.value, currentMonth.value, i),
      day: i,
      currentMonth: true
    });
  }

  // Next month padding
  const remainingCells = 42 - days.length; // 6 rows * 7
  for (let i = 1; i <= remainingCells; i++) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value + 1, i),
       day: i,
       currentMonth: false
     });
  }

  return days;
});

function prev() {
  if (viewMode.value === 'month') {
      prevMonth();
  } else {
      yearPage.value -= 12; // Go back 12 years
  }
}

function next() {
  if (viewMode.value === 'month') {
      nextMonth();
  } else {
      yearPage.value += 12; // Go forward 12 years
  }
}

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
  emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
  emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function toggleViewMode() {
    if (viewMode.value === 'month') {
        viewMode.value = 'year';
        yearPage.value = Math.floor(currentYear.value / 12) * 12; // Center roughly
    } else {
        viewMode.value = 'month';
    }
}

function selectYear(year: number) {
    currentYear.value = year;
    viewMode.value = 'month';
    emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSelected(date: Date) {
  return props.modelValue === formatDate(date);
}

function isToday(date: Date) {
  return formatDate(date) === formatDate(new Date());
}

function hasData(date: Date) {
  if (!props.activityDates) return false;
  return props.activityDates.includes(formatDate(date));
}

function selectDate(date: Date) {
  emit('update:modelValue', formatDate(date));
  emit('close');
}
</script>

<style scoped>
.material-calendar {
  background: #1E1E1E; /* Dark Suraface */
  border-radius: 16px;
  padding: 16px;
  width: 320px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
  font-family: 'Inter', sans-serif;
  border: 1px solid #333;
  user-select: none;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: #fff;
}

.month-label {
  font-weight: 600;
  text-transform: capitalize;
  font-size: 16px;
}
.month-label.clickable {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 8px;
    transition: background 0.2s;
}
.month-label.clickable:hover {
    background: rgba(255,255,255,0.1);
}

.nav-btn {
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-btn:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  text-align: center;
}

.weekday-label {
  font-size: 12px;
  color: #888;
  font-weight: 500;
  margin-bottom: 4px;
}

.day-cell {
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%; /* Circle */
  font-size: 14px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  background: transparent;
}

/* Logic for states */
.day-cell.current-month {
  color: #fff;
}

.day-cell:hover {
  background: rgba(255,255,255,0.1);
}

.day-cell.selected {
  background: #2196F3 !important; /* Primary Blue */
  color: #fff !important;
  font-weight: bold;
}

.day-cell.today {
  border: 1px solid #2196F3;
}

/* Data Indicator logic - Material 3 Style */
/* User asked: "giorni con dati me li metti con bordo colorato di blu" */
/* + background sotto minimal */
.day-cell.has-data:not(.selected) {
    border: 2px solid #2196F3; /* Blue Border */
    background: rgba(33, 150, 243, 0.15); /* Subtle blue background */
    color: #fff;
}

/* Ensure selected state overrides or merges nicely */
.day-cell.selected {
    background: #2196F3 !important;
    color: #fff !important;
    border: 2px solid #2196F3;
    font-weight: bold;
}

/* If filled style requested: */
/*
.day-cell.has-data:not(.selected) {
    background-color: #4CAF50;
    color: white; 
}
*/
/* Let's try to be subtle but clear as Material 3 suggests 'filled' variants */
.day-cell.has-data:not(.selected).current-month {
    /* background-color: #388E3C; Removed fill */
    /* color: #fff; */
    border-color: #388E3C; /* Example alternative if needed */
}

.data-dot {
  display: none; /* User asked for full color, not dot */
}

/* YEAR GRID */
.year-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 10px 0;
}
.year-cell {
    padding: 12px;
    text-align: center;
    border-radius: 8px;
    cursor: pointer;
    color: #aaa;
    transition: all 0.2s;
    font-size: 0.9rem;
    background: rgba(255,255,255,0.05);
}
.year-cell:hover {
    background: rgba(255,255,255,0.15);
    color: #fff;
    transform: scale(1.05);
}
.year-cell.selected {
    border: 1px solid #2196F3;
    color: #2196F3;
    font-weight: bold;
}
.year-cell.current-year {
    background: #2196F3;
    color: #fff;
    font-weight: bold;
}
</style>
